import { z } from 'zod';
import { positiveIntSchema } from '../shared/index.js';
/**
 * Validation schemas and response types for the continuous broadcast lifecycle API.
 *
 * A broadcast is a single continuous Mux live stream that spans one or more sequential
 * meetings on the same encoder. These schemas guard the board-admin-gated lifecycle
 * endpoints (`start`, `switch`, `end`) and the broadcast read endpoints.
 */
/**
 * Broadcast status values, mirroring the `BroadcastStatus` Prisma enum.
 */
export var BroadcastStatus;
(function (BroadcastStatus) {
    BroadcastStatus["Active"] = "active";
    BroadcastStatus["Ended"] = "ended";
    BroadcastStatus["Splitting"] = "splitting";
    BroadcastStatus["Completed"] = "completed";
    BroadcastStatus["Failed"] = "failed";
})(BroadcastStatus || (BroadcastStatus = {}));
/**
 * Per-segment archived-clip status values, mirroring the `ArchivedClipStatus` Prisma enum.
 */
export var ArchivedClipStatus;
(function (ArchivedClipStatus) {
    ArchivedClipStatus["Pending"] = "pending";
    ArchivedClipStatus["Processing"] = "processing";
    ArchivedClipStatus["Ready"] = "ready";
    ArchivedClipStatus["Failed"] = "failed";
})(ArchivedClipStatus || (ArchivedClipStatus = {}));
/**
 * Request body for `POST /broadcasts/enable`, and for the deprecated one-step `POST /broadcasts/start`
 * alias, which takes the same body.
 *
 * Opens a continuous broadcast on the given encoder in preview, anchored to the first meeting. The
 * meeting is not promoted until `POST /broadcasts/{id}/go-live`.
 */
export const broadcastEnableSchema = z.object({
    mediaEncoderId: positiveIntSchema,
    meetingEventId: positiveIntSchema,
});
/**
 * Request body for `POST /broadcasts/{id}/switch`.
 *
 * Moves the on-air meeting forward to a new meeting, or back to a prior participating one.
 */
export const broadcastSwitchSchema = z.object({
    nextMeetingEventId: positiveIntSchema,
    /**
     * When true, gracefully completes the outgoing on-air meeting as part of the switch (End Current &
     * Switch) instead of leaving it Live for switch-back. The broadcast keeps streaming into the target;
     * only the meeting being left is finalized. This is the reuse seam for the auto-start chain
     * (GOV-1516: "switch to B, end A") and the future clerk-console adjourn-and-switch.
     */
    endCurrent: z.boolean().optional().default(false),
});
/**
 * Query parameters for `GET /broadcasts/active`.
 *
 * Resolves the active broadcast on the given meeting's encoder. Coerces the query string to a number.
 */
export const broadcastActiveQuerySchema = z.object({
    meetingEventId: z.coerce.number().int().positive(),
});
/**
 * Request body for `POST /broadcasts/{id}/regenerate`.
 *
 * The admin scrub-timeline "Apply changes" call: re-cut ONE meeting's clip from the untouched master.
 * It carries `meetingEventId` (the meeting to re-cut) plus any segment offset edits to apply first, so
 * the controller sees each segment's pre-edit start and can shift the meeting's preserved recording
 * timestamps by the boundary delta in the same transaction (a separate PATCH would destroy the old
 * start before regenerate ran). Segments is optional — a bare regenerate re-cuts at the current offsets.
 */
export const broadcastRegenerateSchema = z.object({
    meetingEventId: positiveIntSchema,
    segments: z
        .array(z
        .object({
        id: positiveIntSchema,
        startOffsetSeconds: z.number().min(0).optional(),
        endOffsetSeconds: z.number().min(0).optional(),
    })
        .refine((segment) => segment.startOffsetSeconds === undefined ||
        segment.endOffsetSeconds === undefined ||
        segment.endOffsetSeconds > segment.startOffsetSeconds, { message: 'endOffsetSeconds must be greater than startOffsetSeconds' }))
        .optional()
        .default([]),
});
